import { AlertTriangle, HelpCircle, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ErrorScreenProps {
	title?: string;
	error: Error;
	resetErrorBoundary: () => void;
}

export function ErrorScreen({ title = 'Something went wrong', error }: ErrorScreenProps) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
			<div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
				<div className="flex items-start gap-4">
					<div className="shrink-0">
						<div className="flex size-12 items-center justify-center rounded-full bg-red-100">
							<AlertTriangle className="size-6 text-red-600" />
						</div>
					</div>

					<div className="flex-1">
						<h1 className="mb-2 text-2xl font-semibold text-gray-900">{title}</h1>
						<p className="mb-6 text-gray-600">{error.message}</p>

						<div className="space-y-4">
							<div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
								<HelpCircle className="mt-0.5 size-5 shrink-0 text-blue-600" />
								<div>
									<h3 className="mb-1 font-medium text-gray-900">Need help?</h3>
									<p className="mb-2 text-sm text-gray-600">Visit our help center for troubleshooting guides and FAQs.</p>
									<Link to="/help" className="text-sm font-medium text-blue-600 hover:text-blue-700">
										Go to Help Center →
									</Link>
								</div>
							</div>

							<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
								<h3 className="mb-3 font-medium text-gray-900">Contact Support</h3>
								<div className="space-y-2">
									<div className="flex items-center gap-3 text-sm">
										<Mail className="size-4 text-gray-400" />
										<a href="mailto:support@homehealth.com" className="text-gray-600 hover:text-gray-900">
											support@homehealth.com
										</a>
									</div>
									<div className="flex items-center gap-3 text-sm">
										<Phone className="size-4 text-gray-400" />
										<a href="tel:+18005551234" className="text-gray-600 hover:text-gray-900">
											1-800-555-1234
										</a>
									</div>
								</div>
							</div>
						</div>

						<div className="mt-6 flex gap-3">
							<button
								type="button"
								onClick={() => window.location.reload()}
								className="rounded-md bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
							>
								Refresh Page
							</button>
							<Link
								to="/"
								className="rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50"
							>
								Go to Home
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
